/**
 * SEO Analyzer - Análisis SEO en tiempo real para artículos del blog
 * Fase 4 del Editor de Blog Mejorado
 */

import React, { useMemo } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  FileText, 
  Clock, 
  Hash, 
  Link2, 
  Heading, 
  Type,
  Search,
  BarChart3
} from 'lucide-react';

// Utility function to strip HTML tags
function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Count words in text
function countWords(text) {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

// Calculate reading time (average 200 words per minute in Spanish)
function calculateReadingTime(wordCount) {
  return Math.ceil(wordCount / 200);
}

// Count headings in HTML
function countHeadings(html) {
  if (!html) return { h2: 0, h3: 0, h4: 0 };
  const h2Count = (html.match(/<h2[^>]*>/gi) || []).length;
  const h3Count = (html.match(/<h3[^>]*>/gi) || []).length;
  const h4Count = (html.match(/<h4[^>]*>/gi) || []).length;
  return { h2: h2Count, h3: h3Count, h4: h4Count };
}

// Count internal links
function countInternalLinks(html) {
  if (!html) return 0;
  const internalLinkPattern = /<a[^>]*href=["']\/[^"']*["'][^>]*>/gi;
  return (html.match(internalLinkPattern) || []).length;
}

// Count external links
function countExternalLinks(html) {
  if (!html) return 0;
  const externalLinkPattern = /<a[^>]*href=["']https?:\/\/[^"']*["'][^>]*>/gi;
  return (html.match(externalLinkPattern) || []).length;
}

// Count images
function countImages(html) {
  if (!html) return 0;
  return (html.match(/<img[^>]*>/gi) || []).length;
}

// Calculate keyword density
function calculateKeywordDensity(text, keywords) {
  if (!text || !keywords) return [];
  const textLower = text.toLowerCase();
  const wordCount = countWords(text);
  if (wordCount === 0) return [];
  
  const keywordList = keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k);
  
  return keywordList.map(keyword => {
    const regex = new RegExp(keyword, 'gi');
    const matches = textLower.match(regex) || [];
    const count = matches.length;
    const density = ((count / wordCount) * 100).toFixed(1);
    return { keyword, count, density: parseFloat(density) };
  });
}

// SEO Check Item Component
function SEOCheckItem({ status, label, value, recommendation }) {
  const getStatusIcon = () => {
    switch (status) {
      case 'good':
        return <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
      default:
        return <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />;
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'good':
        return 'bg-emerald-500/10 border-emerald-500/30';
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/30';
      case 'error':
        return 'bg-red-500/10 border-red-500/30';
      default:
        return 'bg-slate-500/10 border-slate-500/30';
    }
  };

  return (
    <div className={`p-2 rounded border ${getBgColor()}`}>
      <div className="flex items-start gap-2">
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-slate-200">{label}</span>
            {value && <span className="text-xs text-slate-400 font-mono">{value}</span>}
          </div>
          {recommendation && (
            <p className="text-xs text-slate-500 mt-0.5">{recommendation}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Main SEO Analyzer Component
export function SEOAnalyzer({ 
  title = '', 
  content = '', 
  metaDescription = '', 
  metaKeywords = '',
  excerpt = '',
  collapsed = false 
}) {
  const analysis = useMemo(() => {
    const plainText = stripHtml(content);
    const wordCount = countWords(plainText);
    const readingTime = calculateReadingTime(wordCount);
    const headings = countHeadings(content);
    const internalLinks = countInternalLinks(content);
    const externalLinks = countExternalLinks(content);
    const images = countImages(content);
    const keywordDensity = calculateKeywordDensity(plainText, metaKeywords);
    
    // Calculate overall score
    let score = 0;
    let maxScore = 0;
    
    // Title checks
    const titleLength = title.length;
    let titleStatus = 'error';
    if (titleLength >= 50 && titleLength <= 60) {
      titleStatus = 'good';
      score += 10;
    } else if (titleLength >= 40 && titleLength <= 70) {
      titleStatus = 'warning';
      score += 5;
    }
    maxScore += 10;
    
    // Meta description checks
    const metaLength = metaDescription.length;
    let metaStatus = 'error';
    if (metaLength >= 120 && metaLength <= 160) {
      metaStatus = 'good';
      score += 10;
    } else if (metaLength >= 100 && metaLength <= 180) {
      metaStatus = 'warning';
      score += 5;
    }
    maxScore += 10;
    
    // Word count checks
    let wordCountStatus = 'error';
    if (wordCount >= 1500) {
      wordCountStatus = 'good';
      score += 15;
    } else if (wordCount >= 800) {
      wordCountStatus = 'warning';
      score += 8;
    } else if (wordCount >= 300) {
      score += 3;
    }
    maxScore += 15;
    
    // Headings structure
    let headingsStatus = 'error';
    if (headings.h2 >= 3 && headings.h3 >= 2) {
      headingsStatus = 'good';
      score += 10;
    } else if (headings.h2 >= 2) {
      headingsStatus = 'warning';
      score += 5;
    }
    maxScore += 10;
    
    // Internal links
    let linksStatus = 'error';
    if (internalLinks >= 3) {
      linksStatus = 'good';
      score += 10;
    } else if (internalLinks >= 1) {
      linksStatus = 'warning';
      score += 5;
    }
    maxScore += 10;
    
    // Keywords
    let keywordsStatus = 'error';
    const hasKeywords = metaKeywords.trim().length > 0;
    if (hasKeywords && keywordDensity.length >= 3) {
      keywordsStatus = 'good';
      score += 10;
    } else if (hasKeywords && keywordDensity.length >= 1) {
      keywordsStatus = 'warning';
      score += 5;
    }
    maxScore += 10;
    
    // Images
    let imagesStatus = 'warning';
    if (images >= 2) {
      imagesStatus = 'good';
      score += 5;
    } else if (images >= 1) {
      score += 3;
    }
    maxScore += 5;
    
    const overallScore = Math.round((score / maxScore) * 100);
    
    return {
      wordCount,
      readingTime,
      headings,
      internalLinks,
      externalLinks,
      images,
      keywordDensity,
      titleLength,
      titleStatus,
      metaLength,
      metaStatus,
      wordCountStatus,
      headingsStatus,
      linksStatus,
      keywordsStatus,
      imagesStatus,
      overallScore
    };
  }, [title, content, metaDescription, metaKeywords]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  if (collapsed) {
    // Compact view - just show score and key metrics
    return (
      <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getScoreBg(analysis.overallScore)}`}>
          <BarChart3 className="w-4 h-4" />
          <span className={`font-bold ${getScoreColor(analysis.overallScore)}`}>
            {analysis.overallScore}%
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-400">
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {analysis.wordCount} palabras
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {analysis.readingTime} min
          </span>
          <span className="flex items-center gap-1">
            <Link2 className="w-3 h-3" />
            {analysis.internalLinks} enlaces
          </span>
          <span className="flex items-center gap-1">
            <Heading className="w-3 h-3" />
            {analysis.headings.h2 + analysis.headings.h3} títulos
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-4">
      {/* Header with Score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-emerald-400 font-medium">
          <Search className="w-4 h-4" />
          Análisis SEO en Tiempo Real
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getScoreBg(analysis.overallScore)}`}>
          <span className="text-xs text-slate-400">Puntuación:</span>
          <span className={`font-bold ${getScoreColor(analysis.overallScore)}`}>
            {analysis.overallScore}%
          </span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center p-2 bg-slate-900/50 rounded">
          <div className="text-lg font-bold text-slate-200">{analysis.wordCount}</div>
          <div className="text-xs text-slate-500">Palabras</div>
        </div>
        <div className="text-center p-2 bg-slate-900/50 rounded">
          <div className="text-lg font-bold text-slate-200">{analysis.readingTime} min</div>
          <div className="text-xs text-slate-500">Lectura</div>
        </div>
        <div className="text-center p-2 bg-slate-900/50 rounded">
          <div className="text-lg font-bold text-slate-200">{analysis.internalLinks}</div>
          <div className="text-xs text-slate-500">Enlaces Int.</div>
        </div>
        <div className="text-center p-2 bg-slate-900/50 rounded">
          <div className="text-lg font-bold text-slate-200">{analysis.headings.h2 + analysis.headings.h3}</div>
          <div className="text-xs text-slate-500">Subtítulos</div>
        </div>
      </div>

      {/* SEO Checks */}
      <div className="space-y-2">
        <SEOCheckItem
          status={analysis.titleStatus}
          label="Longitud del título"
          value={`${analysis.titleLength} caracteres`}
          recommendation={
            analysis.titleStatus === 'good' 
              ? 'Perfecto para Google' 
              : analysis.titleStatus === 'warning'
              ? 'Intenta mantenerlo entre 50-60 caracteres'
              : 'Muy corto o largo. Ideal: 50-60 caracteres'
          }
        />
        
        <SEOCheckItem
          status={analysis.metaStatus}
          label="Meta description"
          value={`${analysis.metaLength} caracteres`}
          recommendation={
            analysis.metaStatus === 'good'
              ? 'Longitud óptima para resultados de Google'
              : analysis.metaStatus === 'warning'
              ? 'Intenta mantenerla entre 120-160 caracteres'
              : analysis.metaLength === 0 
              ? 'Agrega una meta description para mejorar el CTR'
              : 'Ajusta la longitud: 120-160 caracteres ideal'
          }
        />
        
        <SEOCheckItem
          status={analysis.wordCountStatus}
          label="Extensión del contenido"
          value={`${analysis.wordCount} palabras`}
          recommendation={
            analysis.wordCountStatus === 'good'
              ? 'Extensión excelente para SEO'
              : analysis.wordCountStatus === 'warning'
              ? 'Buen contenido. Para mejor ranking, considera 1500+ palabras'
              : 'Contenido muy corto. Los artículos de 1500+ palabras rankean mejor'
          }
        />
        
        <SEOCheckItem
          status={analysis.headingsStatus}
          label="Estructura de encabezados"
          value={`H2: ${analysis.headings.h2}, H3: ${analysis.headings.h3}`}
          recommendation={
            analysis.headingsStatus === 'good'
              ? 'Buena estructura jerárquica'
              : analysis.headingsStatus === 'warning'
              ? 'Agrega más subtítulos H3 para mejor organización'
              : 'Agrega al menos 3 secciones H2 y 2 subsecciones H3'
          }
        />
        
        <SEOCheckItem
          status={analysis.linksStatus}
          label="Enlaces internos"
          value={`${analysis.internalLinks} enlaces`}
          recommendation={
            analysis.linksStatus === 'good'
              ? 'Buen interlinking interno'
              : analysis.linksStatus === 'warning'
              ? 'Agrega más enlaces a otras páginas del sitio'
              : 'Agrega al menos 3 enlaces internos para mejorar SEO'
          }
        />
        
        <SEOCheckItem
          status={analysis.keywordsStatus}
          label="Palabras clave"
          value={analysis.keywordDensity.length > 0 ? `${analysis.keywordDensity.length} keywords` : 'Sin keywords'}
          recommendation={
            analysis.keywordsStatus === 'good'
              ? 'Keywords bien definidas'
              : analysis.keywordsStatus === 'warning'
              ? 'Agrega más palabras clave relevantes'
              : 'Define al menos 3 palabras clave para el artículo'
          }
        />

        <SEOCheckItem
          status={analysis.imagesStatus}
          label="Imágenes"
          value={`${analysis.images} imagen${analysis.images !== 1 ? 'es' : ''}`}
          recommendation={
            analysis.imagesStatus === 'good'
              ? 'Buen uso de imágenes'
              : 'Agrega imágenes relevantes para mejorar engagement'
          }
        />
      </div>

      {/* Keyword Density */}
      {analysis.keywordDensity.length > 0 && (
        <div className="pt-2 border-t border-slate-700">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
            <Hash className="w-3 h-3" />
            Densidad de Keywords
          </div>
          <div className="flex flex-wrap gap-2">
            {analysis.keywordDensity.slice(0, 5).map((kw, idx) => (
              <span 
                key={idx}
                className={`px-2 py-1 rounded text-xs ${
                  kw.density > 0 && kw.density < 3 
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : kw.density >= 3 
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-slate-700 text-slate-400'
                }`}
              >
                {kw.keyword}: {kw.count}x ({kw.density}%)
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Densidad ideal: 1-2.5% por keyword. Mayor a 3% puede ser penalizado.
          </p>
        </div>
      )}
    </div>
  );
}

export default SEOAnalyzer;
